import * as React from 'react';
import { CHART_OPTION_GROUPS, ChartOptionGroupId } from './chartOptionGroups';

export type ChartOptionSelections = Partial<Record<ChartOptionGroupId, string>>;

interface ChartOptionsContextValue {
    // the choice made for the whole dashboard, by group ("time" -> "previous month")
    selections: ChartOptionSelections;
    // goes up with every Apply or Reset, so a chart can tell a new choice from a re-render
    version: number;
    // goes up when a choice is taken away: the charts start again from their own defaults (they are mounted afresh), then the remaining choices apply
    resetVersion: number;
    // the feature names of the charts in the current panel's layout (known before the charts are mounted)
    featureNames: string[];
    registerFeatureNames: (featureNames: string[]) => void;
    applySelections: (selections: ChartOptionSelections) => void;
    resetSelections: () => void;
}

// Outside a dashboard there is no provider: nothing is selected and every call does nothing, so a chart shown anywhere else is unaffected.
const noOperation = () => undefined;
const defaultValue: ChartOptionsContextValue = {
    selections: {},
    version: 0,
    resetVersion: 0,
    featureNames: [],
    registerFeatureNames: noOperation,
    applySelections: noOperation,
    resetSelections: noOperation,
};

const ChartOptionsContext = React.createContext<ChartOptionsContextValue>(defaultValue);

export const useChartOptions = () => React.useContext(ChartOptionsContext);

const STORAGE_PREFIX = 'rvChartOptions:';

// STORED IN localStorage FOR NOW: it survives a sign out and a new visit, but it is per browser (a second computer starts with nothing).
// When the backend has a place for per-user settings, replace readStoredSelections / writeStoredSelections; nothing else needs to change.
// Only a choice this version knows is read back (a stored value can be old, edited or damaged), so a bad entry is just ignored.
const readStoredSelections = (dashboardType?: number): ChartOptionSelections => {
    if (dashboardType === undefined) {
        return {};
    }
    try {
        const storedText = window.localStorage.getItem(STORAGE_PREFIX + dashboardType);
        const parsed = storedText ? JSON.parse(storedText) : null;
        const selections: ChartOptionSelections = {};
        if (parsed && typeof parsed === 'object') {
            CHART_OPTION_GROUPS.forEach((group) => {
                const storedKey = parsed[group.id];
                if (typeof storedKey === 'string' && group.choices.some((choice) => choice.key === storedKey)) {
                    selections[group.id] = storedKey;
                }
            });
        }
        return selections;
    } catch {
        return {};
    }
};

const writeStoredSelections = (dashboardType: number | undefined, selections: ChartOptionSelections) => {
    if (dashboardType === undefined) {
        return;
    }
    try {
        if (Object.keys(selections).some((groupId) => !!selections[groupId as ChartOptionGroupId])) {
            window.localStorage.setItem(STORAGE_PREFIX + dashboardType, JSON.stringify(selections));
        } else {
            window.localStorage.removeItem(STORAGE_PREFIX + dashboardType);
        }
    } catch {
        // storage unavailable (private window, quota) - the choice simply lasts until the page is reloaded
    }
};

interface ChartOptionsProviderProps {
    // The kind of dashboard (DashboardTypeEnum: Firm, Lead, Fee Earner, Task, Client): each kind remembers its own choice, so the Firm Dashboard's
    // "Previous Month" never reaches the Task Dashboard. Without it the choice is not remembered.
    dashboardType?: number;
}

// Wraps a dashboard page: its "Chart options" drawer sets one period for every chart in the panel that has it, and each chart applies it
// to its own switch (see useApplyChartOptions). The choice belongs to the dashboard, not the panel: it is kept when another panel is
// opened (its charts apply it as they mount) and when the user comes back later.
export const ChartOptionsProvider: React.FC<ChartOptionsProviderProps> = ({ dashboardType, children }) => {
    const [initialSelections] = React.useState(() => readStoredSelections(dashboardType));
    const [selections, setSelections] = React.useState<ChartOptionSelections>(initialSelections);
    // a remembered choice counts as a choice already made: the charts apply it as they mount
    const [version, setVersion] = React.useState(Object.keys(initialSelections).length > 0 ? 1 : 0);
    const [resetVersion, setResetVersion] = React.useState(0);
    const [featureNames, setFeatureNames] = React.useState<string[]>([]);

    const featureNamesRef = React.useRef<string[]>([]);
    const selectionsRef = React.useRef<ChartOptionSelections>(initialSelections);

    const registerFeatureNames = React.useCallback((names: string[]) => {
        if (featureNamesRef.current.join('|') === names.join('|')) {
            return;
        }
        featureNamesRef.current = names;
        setFeatureNames(names);
    }, []);

    const applySelections = React.useCallback((nextSelections: ChartOptionSelections) => {
        const isAChoiceTakenAway = (Object.keys(selectionsRef.current) as ChartOptionGroupId[]).some(
            (groupId) => !!selectionsRef.current[groupId] && !nextSelections[groupId],
        );
        selectionsRef.current = nextSelections;
        setSelections(nextSelections);
        writeStoredSelections(dashboardType, nextSelections);
        if (isAChoiceTakenAway) {
            setResetVersion((previousVersion) => previousVersion + 1);
        }
        setVersion((previousVersion) => previousVersion + 1);
    }, [dashboardType]);

    const resetSelections = React.useCallback(() => {
        selectionsRef.current = {};
        setSelections({});
        writeStoredSelections(dashboardType, {});
        setResetVersion((previousVersion) => previousVersion + 1);
        setVersion((previousVersion) => previousVersion + 1);
    }, [dashboardType]);

    const value = React.useMemo(
        () => ({ selections, version, resetVersion, featureNames, registerFeatureNames, applySelections, resetSelections }),
        [selections, version, resetVersion, featureNames, registerFeatureNames, applySelections, resetSelections],
    );

    return <ChartOptionsContext.Provider value={value}>{children}</ChartOptionsContext.Provider>;
};
